import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tenantApi, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import StepIndicator from "@/components/StepIndicator";
import SectionCard from "@/components/SectionCard";
import BrandingPreview from "@/components/BrandingPreview";
import BrandingFields from "@/components/BrandingFields";
import PortalConfigFields from "@/components/PortalConfigFields";
import { FeatureToggles, CustomLabels } from "@/components/FeatureFields";
import RoleMappingFields, { DEFAULT_MAPPING } from "@/components/RoleMappingFields";
import BrandLogo from "@/components/BrandLogo";
import { defaultFeatureConfig, BRAND_SWATCHES, normalizeDomain, paletteFromWebsite } from "@/lib/tenant-theme";
import { Check, Loader2, ToggleRight, Tags, Eye } from "lucide-react";

const STEPS = [
  { title: "Tenant Info", description: "Basic details" },
  { title: "Branding", description: "Logo, colors, fonts" },
  { title: "Portal Config", description: "Portals & modules" },
  { title: "Access & Roles", description: "Role mapping" },
  { title: "Review", description: "Confirm & launch" },
];

// Human labels + one-liners for the portals a tenant can activate. Keep the
// keys in sync with the backend portal registry (PORTAL_CLIENT_IDS).
const PORTAL_LABELS = {
  talent: "Talent",
  hub: "Hub",
  serviqo: "ServiQO",
};
const PORTAL_DESCRIPTIONS = {
  talent: "Staffing & recruitment",
  hub: "Employee portal",
  serviqo: "Email-to-case service desk",
};

/**
 * Public tenant-onboarding wizard matching the reviewed reference screens:
 * Tenant Info (name/code/admin/plan) -> Branding (logo/favicon/colors/
 * typography) -> Portal Configuration (per-portal names/subdomains/nav
 * modules + feature toggles + custom labels) -> Access & Roles (role
 * mapping grid) -> Review & launch, with a live Desktop/Tablet/Mobile
 * two-portal preview throughout.
 *
 * REAL CONSTRAINT (unchanged): tis-auth-tenant-service's config write
 * (PATCH /tenants/:id/configuration) needs an authenticated OWN/TSA
 * principal, which does not exist during public signup. So branding +
 * feature/portal config collected here are STAGED in localStorage keyed
 * by tenantId and written to Auth config the first time the new admin
 * logs in (see AdminBranding.jsx) — the single source of truth both
 * Talent and Hub read. Not silently dropped; not pretending the public
 * signup endpoint accepts them (it does not).
 */
export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Portals the tenant chooses to activate. Independent of plan tier: the
  // plan's included_portals is what's AVAILABLE; the tenant picks one or
  // more of them. Defaults to all available portals when a plan is chosen,
  // and the tenant can deselect down to any non-empty subset.
  const [selectedPortals, setSelectedPortals] = useState([]);

  const [form, setForm] = useState({ companyName: "", tenantCode: "", email: "", domain: "" });
  const [branding, setBranding] = useState({
    shortName: "",
    colors: { primary: BRAND_SWATCHES[0].primary, secondary: BRAND_SWATCHES[0].secondary, accent: BRAND_SWATCHES[0].accent },
    logoUrl: null,
    faviconUrl: null,
  });
  const [featureConfig, setFeatureConfig] = useState(defaultFeatureConfig());
  const [fieldError, setFieldError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [websiteStatus, setWebsiteStatus] = useState("idle"); // idle|detecting|detected|notfound
  const [autoPulledFor, setAutoPulledFor] = useState(null); // domain we already auto-pulled
  const [brandingTouched, setBrandingTouched] = useState(false); // user hand-edited colors?

  const websiteDomain = normalizeDomain(form.domain);

  const pullWebsitePalette = useCallback(
    async (domain) => {
      if (!domain) return;
      setWebsiteStatus("detecting");
      const result = await paletteFromWebsite(domain);
      if (result) {
        // Prefill colors from the tenant's own website, and use their
        // favicon as the logo if they haven't uploaded one.
        setBranding((b) => ({
          ...b,
          colors: { ...b.colors, ...result.palette },
          logoUrl: b.logoUrl || result.faviconUrl,
          faviconUrl: b.faviconUrl || result.faviconUrl,
        }));
        setWebsiteStatus("detected");
      } else {
        setWebsiteStatus("notfound");
      }
    },
    [],
  );

  // AUTOMATIC: once a valid domain is known and the user reaches the
  // Branding step, pull the tenant's brand colors from their website and
  // prefill — unless they've already hand-edited colors, which we never
  // overwrite. Re-runs if the domain changes to a different site.
  useEffect(() => {
    if (step === 2 && websiteDomain && websiteDomain !== autoPulledFor && !brandingTouched) {
      setAutoPulledFor(websiteDomain);
      pullWebsitePalette(websiteDomain);
    }
  }, [step, websiteDomain, autoPulledFor, brandingTouched, pullWebsitePalette]);

  const onBrandingChange = (next) => {
    setBrandingTouched(true);
    setBranding(next);
  };

  useEffect(() => {
    tenantApi
      .get("/plans")
      .then((res) => {
        setPlans(res.data);
        setPlansLoading(false);
      })
      .catch((err) => {
        setPlansError(errorMessage(err) || "We couldn't load the plans. Please try again.");
        setPlansLoading(false);
      });
  }, []);

  // What the rest of the wizard (branding/portal-config/preview) renders is
  // the tenant's SELECTED portals, not the plan's full available list.
  const availablePortals = selectedPlan?.included_portals || ["talent", "hub"];
  const includedPortals = selectedPortals.length ? selectedPortals : availablePortals;
  const togglePortal = (portal) =>
    setSelectedPortals((prev) =>
      prev.includes(portal) ? prev.filter((p) => p !== portal) : [...prev, portal],
    );
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const step1Valid = selectedPlan && includedPortals.length > 0 && form.companyName.trim() && form.tenantCode.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await tenantApi.post(
        "/signup",
        {
          company_name: form.companyName,
          primary_contact_email: form.email,
          plan_code: selectedPlan.plan_code,
          selected_portals: includedPortals,
          domain: form.domain || undefined,
          payment_method_token: "tok_sandbox_placeholder",
        },
        { headers: { "Idempotency-Key": crypto.randomUUID() } },
      );
      const tenantId = res.data.tenant_id;
      // Stage branding + feature config for AdminBranding.jsx to PATCH to
      // Auth config once the new admin logs in (single source of truth).
      localStorage.setItem(
        `staged_branding_${tenantId}`,
        JSON.stringify({ branding, featureConfig: { ...featureConfig, roleMapping: featureConfig.roleMapping || DEFAULT_MAPPING, tenantCode: form.tenantCode } }),
      );
      navigate(`/signup/pending?tenantId=${tenantId}`);
    } catch (err) {
      setSubmitError(errorMessage(err) || "We couldn't complete signup. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const showPreview = step >= 2 && step <= 4;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-4 sm:px-8">
        <BrandLogo size="md" />
        <a href="/login" className="text-sm font-medium text-primary hover:underline">Sign in</a>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div>
          <h1 className="font-heading text-2xl font-bold">Create a new tenant</h1>
          <p className="mt-1 text-muted-foreground">Set up your organization and configure the Talent and Hub portal experience.</p>
        </div>
        <div className="mt-8">
          <StepIndicator steps={STEPS} current={step} />
        </div>

        {fieldError && (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="wizard-field-error">
            {fieldError}
          </div>
        )}

        <div className={showPreview ? "mt-8 grid gap-8 lg:grid-cols-[1fr_400px]" : "mt-8"}>
          <div className="space-y-6">
            {step === 1 && (
              <>
                <SectionCard step={1} title="Tenant Basics" subtitle="Basic information about the tenant organization.">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="companyName">Tenant name *</Label>
                      <Input id="companyName" className="mt-1.5" data-testid="signup-company-name"
                        value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="tenantCode">Tenant code *</Label>
                      <Input id="tenantCode" className="mt-1.5" data-testid="signup-tenant-code" placeholder="acme"
                        value={form.tenantCode}
                        onChange={(e) => setForm({ ...form, tenantCode: e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase() })} />
                      <p className="mt-1 text-xs text-muted-foreground">Used for internal reference (e.g. acme).</p>
                    </div>
                    <div>
                      <Label htmlFor="email">Admin contact *</Label>
                      <Input id="email" type="email" className="mt-1.5" data-testid="signup-admin-email"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="domain">Company domain (optional)</Label>
                      <Input id="domain" className="mt-1.5" placeholder="acme.com" data-testid="signup-domain"
                        value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard step={2} title="Subscription plan" subtitle="Choose a plan. Upgrade or change anytime.">
                  {plansLoading && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
                  {plansError && <p className="text-sm text-destructive" data-testid="plans-error">{plansError}</p>}
                  <div className="grid gap-4 sm:grid-cols-3">
                    {plans.map((plan) => {
                      const active = selectedPlan?.plan_code === plan.plan_code;
                      return (
                        <button
                          type="button"
                          key={plan.plan_code}
                          onClick={() => { setSelectedPlan(plan); setSelectedPortals(plan.included_portals || []); }}
                          data-testid={`select-plan-${plan.plan_code}`}
                          className={`flex flex-col rounded-lg border p-4 text-left transition-all ${active ? "border-primary ring-2 ring-primary/30 bg-accent/30" : "border-border hover:border-primary/40 hover:shadow-sm"}`}
                        >
                          <div className="flex items-baseline justify-between">
                            <span className="font-heading text-base font-semibold">{plan.name}</span>
                            {active && <Check className="h-4 w-4 text-primary" />}
                          </div>
                          <span className="mt-0.5 text-sm text-muted-foreground">
                            {plan.price_cents === 0 ? "Contact sales" : `$${(plan.price_cents / 100).toFixed(0)}/${plan.billing_cycle === "monthly" ? "mo" : "yr"}`}
                          </span>
                          <div className="mt-3 flex-1 space-y-1.5">
                            {plan.included_portals.map((portal) => (
                              <div key={portal} className="flex items-center gap-2 text-sm">
                                <Check className="h-4 w-4 text-emerald-600" />
                                <span className="capitalize">{portal} Portal</span>
                              </div>
                            ))}
                          </div>
                          {plan.seat_limit && <Badge variant="secondary" className="mt-3 self-start">Up to {plan.seat_limit} users</Badge>}
                        </button>
                      );
                    })}
                  </div>
                </SectionCard>

                {selectedPlan && (
                  <SectionCard
                    step={null}
                    title="Portals to activate"
                    subtitle="Choose which portals this tenant uses. Users can only sign in to the portals you activate here. Select one or more."
                  >
                    <div className="grid gap-3 sm:grid-cols-3" data-testid="portal-picker">
                      {availablePortals.map((portal) => {
                        const on = includedPortals.includes(portal);
                        const label = PORTAL_LABELS[portal] || `${portal} Portal`;
                        const desc = PORTAL_DESCRIPTIONS[portal] || "";
                        return (
                          <button
                            type="button"
                            key={portal}
                            onClick={() => togglePortal(portal)}
                            aria-pressed={on}
                            data-testid={`toggle-portal-${portal}`}
                            className={`flex flex-col rounded-lg border p-4 text-left transition-all ${on ? "border-primary ring-2 ring-primary/30 bg-accent/30" : "border-border hover:border-primary/40"}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{label}</span>
                              {on && <Check className="h-4 w-4 text-primary" />}
                            </div>
                            {desc && <span className="mt-1 text-xs text-muted-foreground">{desc}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {includedPortals.length === 0 && (
                      <p className="mt-2 text-sm text-destructive" data-testid="portal-picker-error">
                        Select at least one portal to continue.
                      </p>
                    )}
                  </SectionCard>
                )}

                <div>
                  <Button disabled={!step1Valid} onClick={next} data-testid="step1-continue-btn">Continue</Button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <SectionCard step={2} title="Branding" subtitle="Customize the look and feel for both portals.">
                  <BrandingFields
                    branding={branding}
                    onChange={onBrandingChange}
                    onError={setFieldError}
                    companyName={form.companyName}
                    website={websiteDomain}
                    websiteStatus={websiteStatus}
                    onPullFromWebsite={() => pullWebsitePalette(websiteDomain)}
                  />
                </SectionCard>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={back}>Back</Button>
                  <Button onClick={next} data-testid="step2-continue-btn">Continue</Button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <SectionCard step={3} title="Portal Configuration" subtitle="Configure both portals, modules and feature toggles.">
                  <PortalConfigFields featureConfig={featureConfig} onChange={setFeatureConfig} portals={includedPortals} branding={branding} />
                </SectionCard>
                <SectionCard icon={ToggleRight} title="Feature Toggles" subtitle="Select the features to enable for this tenant.">
                  <FeatureToggles featureConfig={featureConfig} onChange={setFeatureConfig} />
                </SectionCard>
                <SectionCard icon={Tags} title="Custom Fields and Labels" subtitle="Rename key terms to match your organization's language.">
                  <CustomLabels featureConfig={featureConfig} onChange={setFeatureConfig} />
                </SectionCard>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={back}>Back</Button>
                  <Button onClick={next} data-testid="step3-continue-btn">Continue</Button>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <SectionCard step={4} title="Access & Roles" subtitle="Set up roles and portal permissions.">
                  <RoleMappingFields featureConfig={featureConfig} onChange={setFeatureConfig} portals={includedPortals} />
                </SectionCard>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={back}>Back</Button>
                  <Button onClick={next} data-testid="step4-continue-btn">Continue</Button>
                </div>
              </>
            )}

            {step === 5 && (
              <>
                <SectionCard step={5} title="Review & launch" subtitle="Here's what you've configured. You can change anything later.">
                  <div className="space-y-3 text-sm">
                    {[
                      ["Tenant", form.companyName],
                      ["Tenant code", form.tenantCode],
                      ["Plan", selectedPlan?.name],
                      ["Admin", form.email],
                      ["Portals", includedPortals.join(", ")],
                      ["Enabled modules", `${Object.values(featureConfig.modules).filter(Boolean).length} on`],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-border pb-2 last:border-0">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium capitalize">{v}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                {submitError && (
                  <div data-testid="signup-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {submitError}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={back}>Back</Button>
                  <Button disabled={submitting} onClick={submit} data-testid="signup-submit-btn">
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Create tenant
                  </Button>
                  <span className="text-xs text-muted-foreground">Sandbox billing only — no real charge.</span>
                </div>
              </>
            )}
          </div>

          {showPreview && (
            <div>
              <div className="sticky top-6 rounded-xl border border-border bg-card p-4 shadow-sm">
                <div className="mb-1 flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Live Preview</p>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">See how your tenant will look across both portals.</p>
                <BrandingPreview branding={branding} portals={includedPortals} featureConfig={featureConfig} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
